class MonsgaProductVideos extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.items = [...this.querySelectorAll('[data-mpv-item]')];
    this.featureMedia = this.querySelector('[data-mpv-feature-media]');
    this.featureTitle = this.querySelector('[data-mpv-feature-title]');
    this.featureAuthor = this.querySelector('[data-mpv-feature-author]');
    this.featureSubscribers = this.querySelector('[data-mpv-feature-subscribers]');
    this.featureAvatar = this.querySelector('[data-mpv-feature-avatar]');
    this.featureDuration = this.querySelector('[data-mpv-feature-duration]');
    this.dialog = this.querySelector('[data-mpv-dialog]');
    this.dialogBody = this.querySelector('[data-mpv-dialog-body]');
    this.dialogTitle = this.querySelector('[data-mpv-dialog-title]');
    this.dialogLink = this.querySelector('[data-mpv-dialog-link]');
    this.playButtons = [...this.querySelectorAll('[data-mpv-play]')];
    this.sourceNote = this.querySelector('[data-mpv-source-note]');
    this.previewTimer = null;
    this.bindYoutubeThumbnailFallbacks(this);

    this.items.forEach((item) => {
      item.addEventListener('click', () => {
        this.select(item, false);
        this.open();
      });
      item.addEventListener('pointerenter', () => {
        if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        this.select(item, false);
        this.queuePreview(item);
      });
      item.addEventListener('pointerleave', () => this.stopPreview());
      item.addEventListener('keydown', (event) => this.onItemKeydown(event, item));
    });
    this.querySelectorAll('[data-mpv-play]').forEach((button) => button.addEventListener('click', () => this.open()));
    this.featureMedia?.addEventListener('pointerenter', () => this.queuePreview(this.currentItem));
    this.featureMedia?.addEventListener('pointerleave', () => this.stopPreview());
    this.querySelector('[data-mpv-close]')?.addEventListener('click', () => this.close());
    this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); });
    this.dialog?.addEventListener('close', () => this.clearDialog());
    this.dialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.close();
    });

    const current = this.items.find((item) => item.getAttribute('aria-current') === 'true') || this.items[0];
    if (current) this.select(current, false);
  }

  select(item, focus = true) {
    this.stopPreview();
    this.currentItem = item;
    this.items.forEach((candidate) => candidate.setAttribute('aria-current', String(candidate === item)));
    const preview = item.querySelector('template[data-mpv-preview]');
    if (preview && this.featureMedia) {
      this.featureMedia.querySelectorAll('[data-mpv-dynamic]').forEach((node) => node.remove());
      const fragment = preview.content.cloneNode(true);
      [...fragment.children].forEach((node) => node.setAttribute('data-mpv-dynamic', ''));
      this.featureMedia.prepend(fragment);
      this.bindYoutubeThumbnailFallbacks(this.featureMedia);
    }
    if (this.featureTitle) this.featureTitle.textContent = item.dataset.title || '';
    if (this.featureAuthor) this.featureAuthor.textContent = item.dataset.author || '';
    if (this.featureSubscribers) this.featureSubscribers.textContent = item.dataset.subscribers || '';
    const avatar = item.querySelector('template[data-mpv-avatar]');
    if (this.featureAvatar && avatar) this.featureAvatar.replaceChildren(avatar.content.cloneNode(true));
    if (this.featureDuration) this.featureDuration.textContent = item.dataset.duration || '';
    const playable = item.dataset.playable === 'true';
    this.playButtons.forEach((button) => {
      button.disabled = !playable;
      button.setAttribute('aria-disabled', String(!playable));
    });
    if (this.sourceNote) {
      this.sourceNote.hidden = playable;
      this.sourceNote.textContent = this.dataset.sourcePendingLabel || 'Cover preview only · video source pending';
    }
    if (focus) item.focus({ preventScroll: true });
  }

  onItemKeydown(event, item) {
    if (!['ArrowDown', 'ArrowUp', 'ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = this.items.indexOf(item);
    let next = index;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % this.items.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + this.items.length) % this.items.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = this.items.length - 1;
    this.select(this.items[next]);
  }

  open() {
    if (!this.currentItem || !this.dialog || !this.dialogBody) return;
    if (this.currentItem.dataset.playable !== 'true') {
      if (this.sourceNote) this.sourceNote.hidden = false;
      return;
    }
    const template = this.currentItem.querySelector('template[data-mpv-player]');
    if (!template) return;
    this.stopPreview();
    this.dialogBody.replaceChildren(template.content.cloneNode(true));
    if (this.dialogTitle) this.dialogTitle.textContent = this.currentItem.dataset.title || 'Video';
    if (this.dialogLink) {
      const platformUrl = this.currentItem.dataset.platformUrl || '';
      this.dialogLink.href = platformUrl || '#';
      this.dialogLink.dataset.visible = String(Boolean(platformUrl));
      this.dialogLink.firstChild.textContent = this.currentItem.dataset.platformLabel || 'Watch on the original platform';
    }
    this.lastTrigger = document.activeElement;
    if (this.dialog.showModal) this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
    this.dialogBody.querySelector('video')?.play().catch(() => {});
  }

  close() {
    if (!this.dialog) return;
    if (this.dialog.close && this.dialog.open) this.dialog.close();
    else {
      this.dialog.removeAttribute('open');
      this.clearDialog();
    }
    this.lastTrigger?.focus?.();
  }

  clearDialog() {
    this.dialogBody?.querySelectorAll('video').forEach((video) => video.pause());
    this.dialogBody?.replaceChildren();
  }

  bindYoutubeThumbnailFallbacks(root) {
    root.querySelectorAll('img[data-mpv-youtube-thumbnail]').forEach((image) => {
      if (image.dataset.mpvFallbackBound === 'true') return;
      image.dataset.mpvFallbackBound = 'true';
      const hasLocalCover = () => image.dataset.mpvFallbackSrc && image.dataset.mpvFallbackUsed !== 'true';
      const useLocalCover = () => {
        image.dataset.mpvFallbackUsed = 'true';
        image.src = image.dataset.mpvFallbackSrc;
      };
      const advance = () => {
        if (image.src.includes('/maxresdefault.jpg')) {
          // The curated local cover is a full-size 16:9 frame, so it beats the
          // 640x480 sddefault and saves two more failed ytimg requests.
          if (hasLocalCover()) useLocalCover();
          else image.src = image.src.replace('/maxresdefault.jpg', '/sddefault.jpg');
        } else if (image.src.includes('/sddefault.jpg')) {
          image.src = image.src.replace('/sddefault.jpg', '/hqdefault.jpg');
        } else if (hasLocalCover()) {
          useLocalCover();
        } else {
          image.remove();
        }
      };
      // ponytail: YouTube answers a missing thumbnail size with HTTP 200 and a 120x90 grey
      // placeholder instead of 404, so 'error' never fires. Treat <=120px as missing.
      const advanceOnPlaceholder = () => {
        if (image.naturalWidth > 0 && image.naturalWidth <= 120) advance();
      };
      image.addEventListener('error', advance);
      image.addEventListener('load', advanceOnPlaceholder);
      if (image.complete) advanceOnPlaceholder();
    });
  }

  queuePreview(item) {
    if (!item || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => this.startPreview(item), 160);
  }

  startPreview(item) {
    const template = item?.querySelector('template[data-mpv-hover]');
    if (!template || !this.featureMedia || !template.content.childElementCount) return;
    this.stopPreview();
    const layer = document.createElement('span');
    layer.className = 'mpv__hover-preview';
    layer.dataset.mpvHoverActive = '';
    layer.append(template.content.cloneNode(true));
    this.featureMedia.prepend(layer);
    layer.querySelector('video')?.play().catch(() => {});
  }

  stopPreview() {
    clearTimeout(this.previewTimer);
    this.featureMedia?.querySelectorAll('[data-mpv-hover-active]').forEach((node) => {
      node.querySelectorAll('video').forEach((video) => video.pause());
      node.remove();
    });
  }
}

if (!customElements.get('monsga-product-videos')) {
  customElements.define('monsga-product-videos', MonsgaProductVideos);
}
